import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Loader2, Play, Download, CheckCircle2, Building2, DollarSign, Users, Footprints, Ticket, Info } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PERIODS = [
  { key: "mtd", label: "Month to date" },
  { key: "last_month", label: "Last month" },
  { key: "wtd", label: "Week to date" },
  { key: "last_week", label: "Last week" },
  { key: "custom", label: "Custom range" },
];
const METHODS = [
  { v: "bank_transfer", l: "Bank transfer" },
  { v: "stripe", l: "Stripe (Connect)" },
  { v: "cash", l: "Cash / cheque" },
  { v: "credit", l: "Account credit" },
  { v: "other", l: "Other" },
];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="sr-card" style={{ padding: 14, borderLeft: accent ? "3px solid var(--gold)" : "3px solid var(--border)" }}>
      <div className="sr-muted" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}><Icon size={13} /> {label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "var(--navy)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function Settlements() {
  const [tab, setTab] = useState("statement");
  const [period, setPeriod] = useState("mtd");
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [batches, setBatches] = useState(null);
  const [openBatch, setOpenBatch] = useState(null);
  const [payFor, setPayFor] = useState(null); // merchant_id being marked paid
  const [payForm, setPayForm] = useState({ method: "bank_transfer", reference: "" });

  const params = () => (period === "custom" ? { from: range.from, to: range.to } : { period });
  const load = () => {
    setLoading(true);
    sr.settlementSummary(params()).then(setData).catch((e) => { toast.error(e?.response?.data?.detail || "Could not load settlement"); setData(null); }).finally(() => setLoading(false));
  };
  useEffect(() => { if (period !== "custom" || (range.from && range.to)) load(); /* eslint-disable-next-line */ }, [period, range.from, range.to]);
  const loadBatches = () => sr.settlementBatches().then((r) => setBatches(r.batches || [])).catch(() => setBatches([]));
  useEffect(() => { if (tab === "history") loadBatches(); }, [tab]);

  const runSettlement = async () => {
    setRunning(true);
    try {
      const r = await sr.settlementRun({ ...(period === "custom" ? { date_from: range.from, date_to: range.to } : { period }) });
      toast.success("Settlement statement recorded");
      setTab("history"); setOpenBatch(r.batch); loadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run settlement"); }
    finally { setRunning(false); }
  };

  const exportCsv = (batch) => {
    const rows = [["Merchant", "Scope", "POS revenue", "Footfall visits", "Footfall fee", "Media spend", "Media fee", "Coupon liability", "Amount due", "Status", "Method", "Reference"]];
    (batch.statements || []).forEach((s) => rows.push([
      s.merchant_name, s.scope, s.pos_revenue, s.footfall_visits, s.footfall_fee, s.media_spend, s.media_fee, s.loyalty_liability, s.amount_due, s.status, s.method || "", s.reference || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `${batch.label.replace(/[^a-z0-9]+/gi, "_")}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const markPaid = async (merchantId) => {
    try {
      const r = await sr.settlementMarkPaid(openBatch.id, merchantId, payForm);
      setOpenBatch(r.batch); setPayFor(null); setPayForm({ method: "bank_transfer", reference: "" });
      loadBatches(); toast.success("Recorded as settled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark settled"); }
  };

  const t = data?.totals || {};

  return (
    <div style={{ maxWidth: 1080 }} data-testid="settlements-page">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Scale size={22} style={{ color: "var(--blue)" }} />
        <h1 style={{ fontSize: 24, color: "var(--navy)", margin: 0 }}>Settlements</h1>
      </div>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 16 }}>
        {data?.scope === "platform" ? "Every merchant's amount owed to the platform, rolled up for the period."
          : data?.scope === "franchisor" ? "Each store's position plus your brand & media fees."
          : "Your platform statement for the period."}
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button className={`sr-btn sr-btn-sm ${tab === "statement" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setTab("statement")} data-testid="settlements-tab-statement">Statement</button>
        <button className={`sr-btn sr-btn-sm ${tab === "history" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setTab("history")} data-testid="settlements-tab-history">History</button>
      </div>

      {tab === "statement" ? (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {PERIODS.map((p) => (
              <button key={p.key} className={`sr-btn sr-btn-sm ${period === p.key ? "sr-btn-gold" : "sr-btn-ghost"}`} onClick={() => setPeriod(p.key)} data-testid={`settlements-period-${p.key}`}>{p.label}</button>
            ))}
          </div>
          {period === "custom" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <input type="date" className="sr-input" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="settlements-from" style={{ maxWidth: 180 }} />
              <input type="date" className="sr-input" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="settlements-to" style={{ maxWidth: 180 }} />
            </div>
          )}

          {loading ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : !data ? null : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 16 }}>
                <Stat icon={DollarSign} label="Amount due to platform" value={money(t.amount_due)} accent />
                <Stat icon={Building2} label="POS revenue attributed" value={money(t.pos_revenue)} />
                <Stat icon={Footprints} label="Footfall visits" value={Number(t.footfall_visits || 0).toLocaleString()} />
                <Stat icon={Ticket} label="Coupon liability" value={money(t.loyalty_liability)} />
              </div>

              <div className="sr-card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }} data-testid="settlements-table">
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
                    <thead>
                      <tr style={{ background: "#F3F5F8", color: "var(--navy)", textAlign: "left" }}>
                        <th style={{ padding: "10px 12px" }}>Merchant</th>
                        <th style={{ padding: "10px 12px", textAlign: "right" }}>POS rev.</th>
                        <th style={{ padding: "10px 12px", textAlign: "right" }}>Visits</th>
                        <th style={{ padding: "10px 12px", textAlign: "right" }}>Footfall fee</th>
                        <th style={{ padding: "10px 12px", textAlign: "right" }}>Media fee</th>
                        <th style={{ padding: "10px 12px", textAlign: "right" }}>Amount due</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.merchants.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>No activity to settle in this period yet.</td></tr>
                      ) : data.merchants.map((m) => (
                        <tr key={m.merchant_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`settlements-row-${m.merchant_id}`}>
                          <td style={{ padding: "10px 12px", fontWeight: 700, color: "var(--navy)" }}>
                            {m.merchant_name}
                            {m.scope && m.scope !== "tenant" && <span style={{ marginLeft: 6, fontSize: 10, background: "#EAF2FF", color: "var(--blue)", borderRadius: 999, padding: "1px 7px" }}>{m.scope}</span>}
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{money(m.pos_revenue)}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{Number(m.footfall_visits || 0).toLocaleString()}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{money(m.footfall_fee)}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>{money(m.media_fee)}</td>
                          <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, color: "var(--navy)" }}>{money(m.amount_due)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button className="sr-btn sr-btn-primary" onClick={runSettlement} disabled={running || data.merchants.length === 0} data-testid="settlements-run">
                  {running ? <Loader2 size={15} className="sr-spin" /> : <Play size={15} />} Run settlement for this period
                </button>
                <span className="sr-muted" style={{ fontSize: 12 }}>Records a statement per merchant you can mark settled.</span>
              </div>

              <div className="sr-card" style={{ marginTop: 16, background: "#FFFDF5", borderColor: "var(--gold)", display: "flex", gap: 10, fontSize: 12 }}>
                <Info size={16} style={{ color: "var(--gold)", flexShrink: 0 }} />
                <div className="sr-muted">
                  Fees use configurable defaults — <b>{money(data.assumptions?.attribution_fee_per_visit)}</b> per attributed visit and
                  <b> {Math.round((data.assumptions?.media_fee_rate || 0) * 100)}%</b> of managed ad spend. Coupon liability is what merchants honored, shown for reference.
                  Real payouts require per-merchant <b>Stripe Connect</b> onboarding at deploy time — recording a settlement here does not move money.
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {openBatch ? (
            <BatchDetail batch={openBatch} onBack={() => setOpenBatch(null)} onExport={exportCsv}
              payFor={payFor} setPayFor={setPayFor} payForm={payForm} setPayForm={setPayForm} markPaid={markPaid} />
          ) : batches === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
          : batches.length === 0 ? <div className="sr-empty" data-testid="settlements-history-empty">No settlements recorded yet — run one from the Statement tab.</div>
          : (
            <div style={{ display: "grid", gap: 10 }}>
              {batches.map((b) => (
                <button key={b.id} className="sr-card" onClick={() => sr.settlementBatch(b.id).then((r) => setOpenBatch(r.batch))} data-testid={`settlements-batch-${b.id}`}
                  style={{ textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <strong style={{ color: "var(--navy)" }}>{b.label}</strong>
                    <div className="sr-muted" style={{ fontSize: 12 }}>{(b.from || "").slice(0, 10)} → {(b.to || "").slice(0, 10)} · {b.scope}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, color: "var(--navy)" }}>{money(b.totals?.amount_due)}</div>
                    <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 9px", background: b.status === "settled" ? "#DCFCE7" : "#FEF3C7", color: b.status === "settled" ? "#166534" : "#92400E" }}>{b.status === "settled" ? "Settled" : "Open"}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BatchDetail({ batch, onBack, onExport, payFor, setPayFor, payForm, setPayForm, markPaid }) {
  return (
    <div data-testid="settlements-batch-detail">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onBack} data-testid="settlements-batch-back">← Back</button>
        <strong style={{ color: "var(--navy)", fontSize: 16 }}>{batch.label}</strong>
        <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 9px", background: batch.status === "settled" ? "#DCFCE7" : "#FEF3C7", color: batch.status === "settled" ? "#166534" : "#92400E" }}>{batch.status === "settled" ? "Settled" : "Open"}</span>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginLeft: "auto" }} onClick={() => onExport(batch)} data-testid="settlements-export-csv"><Download size={14} /> Export CSV</button>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {(batch.statements || []).map((s) => (
          <div key={s.merchant_id} className="sr-card" data-testid={`settlements-statement-${s.merchant_id}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <strong style={{ color: "var(--navy)" }}>{s.merchant_name}</strong>
                <div className="sr-muted" style={{ fontSize: 12 }}>
                  POS {money(s.pos_revenue)} · {Number(s.footfall_visits || 0).toLocaleString()} visits · media {money(s.media_spend)} · coupons {money(s.loyalty_liability)}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="sr-muted" style={{ fontSize: 11 }}>Amount due</div>
                <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: 18 }}>{money(s.amount_due)}</div>
              </div>
              {s.status === "paid" ? (
                <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#166534", fontWeight: 700, fontSize: 13 }} data-testid={`settlements-paid-${s.merchant_id}`}>
                  <CheckCircle2 size={16} /> Settled{s.method ? ` · ${s.method}` : ""}
                </span>
              ) : (
                <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => setPayFor(s.merchant_id)} data-testid={`settlements-markpaid-${s.merchant_id}`}>Mark settled</button>
              )}
            </div>
            {payFor === s.merchant_id && s.status !== "paid" && (
              <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <select className="sr-select" value={payForm.method} onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))} data-testid="settlements-pay-method" style={{ maxWidth: 200 }}>
                  {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
                <input className="sr-input" placeholder="Reference (optional)" value={payForm.reference} onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))} data-testid="settlements-pay-ref" style={{ flex: 1, minWidth: 160 }} />
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => markPaid(s.merchant_id)} data-testid="settlements-pay-confirm">Record</button>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setPayFor(null)}>Cancel</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
